import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'

const ConversationContext = createContext()

export const useConversations = () => {
  const context = useContext(ConversationContext)
  if (!context) {
    throw new Error('useConversations must be used within a ConversationProvider')
  }
  return context
}

export const ConversationProvider = ({ children, currentUser }) => {
  const [conversations, setConversations] = useState([])
  const [unreadCountsByLawFirm, setUnreadCountsByLawFirm] = useState({})
  const consumer = useRef(null)
  const conversationsSubscription = useRef(null)

  // Initialize ActionCable consumer
  useEffect(() => {
    if (!currentUser) return

    console.log('🔌 ConversationContext: Initializing ActionCable consumer...')
    consumer.current = createConsumer()

    return () => {
      if (consumer.current) {
        consumer.current.disconnect()
      }
    }
  }, [currentUser])

  // Subscribe to conversations channel for real-time updates
  useEffect(() => {
    if (currentUser && consumer.current) {
      console.log('📡 ConversationContext: Subscribing to ConversationsChannel...')

      conversationsSubscription.current = consumer.current.subscriptions.create(
        { channel: 'ConversationsChannel' },
        {
          connected() {
            console.log('🔗 ConversationContext: Connected to ConversationsChannel')
          },
          disconnected() {
            console.log('❌ ConversationContext: Disconnected from ConversationsChannel')
          },
          received(data) {
            console.log('📨 ConversationContext: ConversationsChannel received:', data)

            if (data.type === 'conversation_updated') {
              updateConversationUnreadCount(data.conversation_id, data.unread_count)
            }
          },
        }
      )
    }

    return () => {
      if (conversationsSubscription.current) {
        conversationsSubscription.current.unsubscribe()
      }
    }
  }, [currentUser])

  // Load conversations on mount
  useEffect(() => {
    if (currentUser) {
      loadConversations()
    }
  }, [currentUser])

  const loadConversations = async () => {
    try {
      const response = await fetch('/api/v1/conversations', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        throw new Error('Failed to load conversations')
      }

      const data = await response.json()
      const conversationList = data.data || []
      setConversations(conversationList)

      // Build unread counts by law firm ID
      const countsByLawFirm = {}
      conversationList.forEach(conv => {
        const lawFirmParticipant = conv.attributes?.other_participants?.find(
          p => p.type === 'law_firm'
        )
        if (lawFirmParticipant) {
          countsByLawFirm[lawFirmParticipant.id] = conv.attributes.unread_count || 0
        }
      })
      setUnreadCountsByLawFirm(countsByLawFirm)

      console.log(
        '📊 ConversationContext: Loaded conversations and unread counts:',
        countsByLawFirm
      )
    } catch (err) {
      console.error('ConversationContext: Error loading conversations:', err)
    }
  }

  const updateConversationUnreadCount = (conversationId, unreadCount) => {
    setConversations(prev =>
      prev.map(conv => {
        if (conv.id === conversationId) {
          const updated = {
            ...conv,
            attributes: {
              ...conv.attributes,
              unread_count: unreadCount,
            },
          }

          // Update the law firm specific count
          const lawFirmParticipant = updated.attributes?.other_participants?.find(
            p => p.type === 'law_firm'
          )
          if (lawFirmParticipant) {
            setUnreadCountsByLawFirm(prevCounts => ({
              ...prevCounts,
              [lawFirmParticipant.id]: unreadCount,
            }))
          }

          return updated
        }
        return conv
      })
    )
  }

  const getUnreadCountForLawFirm = lawFirmId => {
    return unreadCountsByLawFirm[lawFirmId] || 0
  }

  const getTotalUnreadCount = () => {
    return Object.values(unreadCountsByLawFirm).reduce((sum, count) => sum + count, 0)
  }

  const value = {
    conversations,
    unreadCountsByLawFirm,
    getUnreadCountForLawFirm,
    getTotalUnreadCount,
    loadConversations,
    updateConversationUnreadCount,
  }

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>
}
